import React from 'react';
import { LexicalEditor } from 'lexical';
import { FORMAT_TEXT_COMMAND } from 'lexical';
import { IS_APPLE } from '../../../utils/environment';
import DropDown, { DropDownItem } from '../../../ui/DropDown';
import { dropDownActiveClass } from './FrontDropdown';

interface TextFormatButtonProps {
  editor: LexicalEditor;
  isEditable: boolean;
  formatState: {
    isBold: boolean;
    isItalic: boolean;
    isUnderline: boolean;
    isStrikethrough: boolean;
    isSubscript: boolean;
    isSuperscript: boolean;
    isCode: boolean;
  };
}

export function FormatButtons({ editor, isEditable, formatState }: TextFormatButtonProps) {
  const { isBold, isItalic, isUnderline, isStrikethrough, isSubscript, isSuperscript, isCode } =
    formatState;

  return (
    <>
      <button
        disabled={!isEditable}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
        className={'toolbar-item spaced ' + (isBold ? 'active' : '')}
        title={IS_APPLE ? 'Bold (⌘B)' : 'Bold (Ctrl+B)'}
        type="button"
        aria-label={`Format text as bold. Shortcut: ${IS_APPLE ? '⌘B' : 'Ctrl+B'}`}
      >
        <i className="format bold" />
      </button>
      <button
        disabled={!isEditable}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
        className={'toolbar-item spaced ' + (isItalic ? 'active' : '')}
        title={IS_APPLE ? 'Italic (⌘I)' : 'Italic (Ctrl+I)'}
        type="button"
        aria-label={`Format text as italics. Shortcut: ${IS_APPLE ? '⌘I' : 'Ctrl+I'}`}
      >
        <i className="format italic" />
      </button>
      <button
        disabled={!isEditable}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
        className={'toolbar-item spaced ' + (isUnderline ? 'active' : '')}
        title={IS_APPLE ? 'Underline (⌘U)' : 'Underline (Ctrl+U)'}
        type="button"
        aria-label={`Format text to underlined. Shortcut: ${IS_APPLE ? '⌘U' : 'Ctrl+U'}`}
      >
        <i className="format underline" />
      </button>
    </>
  );
}

interface AdditionalFormatButtonsProps {
  editor: LexicalEditor;
  isEditable: boolean;
  formatState: {
    isStrikethrough: boolean;
    isSubscript: boolean;
    isSuperscript: boolean;
  };
  clearFormatting: () => void;
}

export function AdditionalFormatButtons({
  editor,
  isEditable,
  formatState,
  clearFormatting
}: AdditionalFormatButtonsProps) {
  const { isStrikethrough, isSubscript, isSuperscript } = formatState;

  return (
    <DropDown
      disabled={!isEditable}
      buttonClassName="toolbar-item spaced"
      buttonLabel=""
      buttonAriaLabel="Formatting options for additional text styles"
      buttonIconClassName="icon dropdown-more"
    >
      <DropDownItem
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}
        className={'item ' + dropDownActiveClass(isStrikethrough)}
        title="Strikethrough"
        aria-label="Format text with a strikethrough"
      >
        <i className="icon strikethrough" />
        <span className="text">Strikethrough</span>
      </DropDownItem>
      <DropDownItem
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript')}
        className={'item ' + dropDownActiveClass(isSubscript)}
        title="Subscript"
        aria-label="Format text with a subscript"
      >
        <i className="icon subscript" />
        <span className="text">Subscript</span>
      </DropDownItem>
      <DropDownItem
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript')}
        className={'item ' + dropDownActiveClass(isSuperscript)}
        title="Superscript"
        aria-label="Format text with a superscript"
      >
        <i className="icon superscript" />
        <span className="text">Superscript</span>
      </DropDownItem>
      <DropDownItem
        onClick={clearFormatting}
        className="item"
        title="Clear text formatting"
        aria-label="Clear all text formatting"
      >
        <i className="icon clear" />
        <span className="text">Clear Formatting</span>
      </DropDownItem>
    </DropDown>
  );
}
